import { types, flow } from 'mobx-state-tree';
import moment from 'moment';
import { GridRowParams, GridValidRowModel } from '@mui/x-data-grid';
import { CUSTOMER_PROFILE_BULK_DELETE_LIMIT, defaultDateFormat } from '../../constants';
import getAppRoot from './utils/getAppRoot';
import getAppEnv from './utils/getAppEnv';
import {
  CreateCustomersFromOrdersResultDtoModelType,
  CreateOrdersFromCustomersResultDtoModelType,
  CustomerProfileDtoModelType,
  VisitDtoModelType,
} from './models';
import {
  createOrdersFromCustomersSuccessNotification,
  createOrdersFromCustomersErrorNotification,
  createOrdersFromCustomersNoProjectNotification,
  createCustomerProfileSuccessNotification,
  createCustomerProfileErrorNotification,
  createCustomersFromOrdersSuccessNotification,
  createCustomersFromOrdersSkippedNotification,
  createCustomersFromOrdersErrorNotification,
  createCustomersFromOrdersNoProjectNotification,
  updateCustomerProfileSuccessNotification,
  updateCustomerProfileErrorNotification,
  deleteCustomerProfileSuccessNotification,
  deleteCustomerProfileErrorNotification,
} from '../../notifications/customerProfiles';
import { CreateCustomerProfileMutation } from './queries/customerProfiles/CreateCustomerProfileMutation';
import { CreateCustomerProfileInputDto, UpdateCustomerProfileInputDto } from './models/RootStore.base';
import i18n from '../../i18n';
import { Pages } from './Router/constants';
import { resolveStoredUnscheduledView } from '../../helpers/getSelectedView';

export type DeleteCustomerProfileResultDto = {
  success: boolean;
  id: string;
};

const CustomerProfiles = types
  .model('CustomerProfiles').props({
    queriedProfileIds: types.array(types.string),
    isLoading: types.optional(types.boolean, false),
    isDeleting: types.optional(types.boolean, false),
    isCreatingOrders: types.optional(types.boolean, false),
    deletingCustomerIds: types.optional(types.array(types.string), []),
    selectedCustomerIds: types.optional(types.array(types.string), []),
    searchTerm: types.optional(types.string, ''),
  })
  .volatile(() => ({ createdOrderBatch: undefined as { organizationId?: string; orders: VisitDtoModelType[] } | undefined }))
  .views(self => ({
    get selectedCustomerCount() {
      return self.selectedCustomerIds.length;
    },
    get hasSelectedCustomers() {
      return self.selectedCustomerIds.length > 0;
    },
    get visibleSelectedCount() {
      return self.selectedCustomerIds.filter(id =>
        self.queriedProfileIds.includes(id),
      ).length;
    },
    get hiddenSelectedCount() {
      return self.selectedCustomerIds.filter(id =>
        !self.queriedProfileIds.includes(id),
      ).length;
    },
  }))
  .actions(self => {
    const setIsLoading = (loading: boolean) => {
      self.isLoading = loading;
    };

    return {
      setQueriedProfileIds(ids: string[]) {
        self.queriedProfileIds.clear();
        self.queriedProfileIds.push(...ids);
      },
      setIsLoading,
      setSelectedCustomerIds(ids: string[]) {
        self.selectedCustomerIds.replace(ids);
      },
      clearSelectedCustomers() {
        self.selectedCustomerIds.clear();
      },
      setSearchTerm(term: string) {
        self.searchTerm = term;
      },
      selectProfile(row: GridRowParams) {
        getAppRoot(self).ui.selectCustomerProfile(row?.row?.id);
      },
      createOrdersFromCustomers: flow(function* (customerIds: string[], deliveryDate?: string | null) {
        if (self.isCreatingOrders) throw new Error('Order creation is already in progress');
        const appRoot = getAppRoot(self);
        const projectId = appRoot.ui.selectedWorkspace?.id;

        if (!projectId) {
          getAppEnv(self).notification(createOrdersFromCustomersNoProjectNotification());
          return Promise.reject(new Error(i18n.t('components:workspace.selectPrompt')));
        }

        const targetPage = appRoot.ui.canViewUnscheduledViews ? Pages.SCHEDULING : Pages.ORDERS;
        const origin = appRoot.router.location;
        const organizationId = appRoot.account?.user?.organization?.id;
        setIsLoading(true);
        self.isCreatingOrders = true;

        try {
          const result: { createOrdersFromCustomers: CreateOrdersFromCustomersResultDtoModelType } = yield appRoot.model
            .mutateCreateOrdersFromCustomers(
              {
                projectId,
                customerIds,
                deliveryDate,
              },
              (qb) => qb
                .createdOrders(visit => visit.id.uuid.name.projectId.customerId.createdOn.customerOrderNumber.routificOrderNumber.deliveryDate)
                .skippedCustomers(skipped => skipped.customerId.reason),
            )
            .currentPromise();

          const createdOrders = result.createOrdersFromCustomers.createdOrders ?? [];
          const createdCount = createdOrders.length;

          if (createdCount > 0) {
            self.createdOrderBatch = { organizationId, orders: createdOrders.slice() };
            const targetDate = createdOrders?.find((o) => o?.deliveryDate)?.deliveryDate
              || localStorage.getItem('selectedWorkspaceDate')
              || appRoot.ui.selectedWorkspace?.startDate
              || moment().format(defaultDateFormat);
            const navigateToOrders = () => {
              const params = new URLSearchParams();
              if (targetPage === Pages.SCHEDULING) {
                params.set('date', targetDate);
                params.set('view', resolveStoredUnscheduledView());
              }
              if (createdCount === 1 && createdOrders?.[0]?.uuid) params.set('order', createdOrders[0].uuid);
              appRoot.router.push({
                pathname: `/workspace/${projectId}/${targetPage}`,
                search: params.toString(),
                ...(params.has('order') && origin ? {
                  state: { customerOrderHistoryReturn: { pathname: origin.pathname, search: origin.search ?? '', state: origin.state } },
                } : {}),
              });
            };

            // Hide the link when the user is already where it would take them:
            // on the Routes page viewing the Not Scheduled tab, or on the Orders page.
            const isOnOrdersPage = appRoot.router?.location?.pathname?.includes(`/${Pages.ORDERS}`);
            getAppEnv(self).notification(
              createOrdersFromCustomersSuccessNotification(
                createdCount,
                (appRoot.ui.isViewingNotScheduledTab || isOnOrdersPage) ? undefined : navigateToOrders,
                targetPage,
              ),
            );
          } else {
            getAppEnv(self).notification(createOrdersFromCustomersErrorNotification(customerIds.length));
          }


          return result.createOrdersFromCustomers;
        } catch (error) {
          getAppEnv(self).notification(createOrdersFromCustomersErrorNotification(customerIds.length));
          throw error;
        } finally {
          setIsLoading(false);
          self.isCreatingOrders = false;
        }
      }),
      createCustomerProfile: flow(function* (input: CreateCustomerProfileInputDto) {
        setIsLoading(true);
        try {
          const result: { createCustomerProfile: CustomerProfileDtoModelType } = yield getAppRoot(self).model
            .mutateCreateCustomerProfile(
              { input },
              CreateCustomerProfileMutation,
            )
            .currentPromise();

          if (result.createCustomerProfile) {
            getAppEnv(self).notification(createCustomerProfileSuccessNotification());
            return result.createCustomerProfile;
          }
          return undefined;
        } catch (error) {
          getAppEnv(self).notification(createCustomerProfileErrorNotification());
          throw error;
        } finally {
          setIsLoading(false);
        }
      }),
      createCustomersFromOrders: flow(function* (orderUuids: string[]) {
        const appRoot = getAppRoot(self);
        const projectId = appRoot.ui.selectedWorkspace?.id;

        if (!projectId) {
          getAppEnv(self).notification(createCustomersFromOrdersNoProjectNotification());
          return Promise.reject(new Error(i18n.t('components:workspace.selectPrompt')));
        }

        setIsLoading(true);

        try {
          const result: { createCustomersFromOrders: CreateCustomersFromOrdersResultDtoModelType } = yield appRoot.model
            .mutateCreateCustomersFromOrders(
              { projectId, orderUuids },
              (qb) => qb
                .createdCustomers((c) => c.orderUuid.customer((cp) => cp.id.name))
                .skippedOrders((s) => s.orderUuid.reason),
            )
            .currentPromise();

          const { createdCustomers, skippedOrders } = result.createCustomersFromOrders;
          const createdCount = createdCustomers?.length || 0;
          const skippedCount = skippedOrders?.length || 0;

          if (createdCount > 0) {
            const navigateToCustomers = () => {
              appRoot.router.push({ pathname: `/workspace/${projectId}/customers` });
            };
            getAppEnv(self).notification(
              createCustomersFromOrdersSuccessNotification(createdCount, navigateToCustomers),
            );
          }

          if (skippedCount > 0 && createdCount > 0) {
            getAppEnv(self).notification(
              createCustomersFromOrdersSkippedNotification(skippedCount),
            );
          } else if (createdCount === 0) {
            const skippedReasons = skippedOrders?.map((o) => o.reason).filter((r): r is string => !!r);
            getAppEnv(self).notification(
              createCustomersFromOrdersErrorNotification(orderUuids.length, skippedReasons),
            );
          }

          return result.createCustomersFromOrders;
        } catch (error) {
          getAppEnv(self).notification(createCustomersFromOrdersErrorNotification(orderUuids.length));
          throw error;
        } finally {
          setIsLoading(false);
        }
      }),
      updateCustomerProfile: flow(function* (input: UpdateCustomerProfileInputDto) {
        setIsLoading(true);
        try {
          const result: { updateCustomerProfile: CustomerProfileDtoModelType } = yield getAppRoot(self).model
            .mutateUpdateCustomerProfile(
              { input },
              CreateCustomerProfileMutation,
              () => {
                // Optimistic update for UI; If backend call fails, this will automatically revert
                const appRoot = getAppRoot(self);
                const existingProfile = appRoot.model.customerProfileDtos?.get(input.id);

                if (existingProfile) {
                  const { id, ...updates } = input;

                  const transformedUpdates: Record<string, any> = {};

                  if (updates.location !== undefined) {
                    transformedUpdates.location = updates.location ? {
                      address: updates.location.address,
                      latitude: updates.location.latitude,
                      longitude: updates.location.longitude,
                    } : null;
                  }

                  if (updates.timeWindows !== undefined) {
                    transformedUpdates.timeWindows = updates.timeWindows?.map(tw => ({
                      startTime: tw.startTime,
                      endTime: tw.endTime,
                    })) || null;
                  }

                  // Merge existing profile with all updates
                  // This automatically handles any field in UpdateCustomerProfileInputDto without manual listing.
                  // Only need to update optomistic if we need to do any deconstructing like location or time windows.
                  const optimisticProfile = {
                    ...existingProfile,
                    ...updates,
                    ...transformedUpdates,
                  };

                  appRoot.model.customerProfileDtos.set(input.id, optimisticProfile as CustomerProfileDtoModelType);
                }
              },
            )
            .currentPromise();

          if (result.updateCustomerProfile) {
            getAppEnv(self).notification(updateCustomerProfileSuccessNotification());
            return result.updateCustomerProfile;
          }
          return undefined;
        } catch (error) {
          getAppEnv(self).notification(updateCustomerProfileErrorNotification());
          throw error;
        } finally {
          setIsLoading(false);
        }
      }),
      deleteCustomerProfiles: flow(function* (ids: string[]) {
        if (self.isDeleting) throw new Error('Customer deletion is already in progress');
        const customerIds = [...new Set(ids)];
        if (!ids.length || ids.length > CUSTOMER_PROFILE_BULK_DELETE_LIMIT || ids.some(id => typeof id !== 'string' || !id.trim())) {
          throw new Error(`Select between 1 and ${CUSTOMER_PROFILE_BULK_DELETE_LIMIT} customers.`);
        }
        self.isDeleting = true;
        self.deletingCustomerIds.replace(customerIds);
        try {
          const appRoot = getAppRoot(self);
          const response: { deleteCustomerProfiles: { deletedIds: string[]; count: number } } = yield appRoot.model
            .mutateDeleteCustomerProfiles({ ids: customerIds }, qb => qb.deletedIds.count)
            .currentPromise();
          const result = response.deleteCustomerProfiles;
          if (!result || result.count !== customerIds.length || !Array.isArray(result.deletedIds)
            || new Set(result.deletedIds).size !== customerIds.length || result.deletedIds.length !== customerIds.length
            || result.deletedIds.some(id => !customerIds.includes(id))) {
            throw new Error('Customer deletion could not be confirmed');
          }
          const deleted = new Set(result.deletedIds);
          if (appRoot.ui.selectedCustomerProfile && deleted.has(appRoot.ui.selectedCustomerProfile.id)) {
            appRoot.ui.selectCustomerProfile(null);
          }
          self.selectedCustomerIds.replace(self.selectedCustomerIds.filter(id => !deleted.has(id)));
          self.queriedProfileIds.replace(self.queriedProfileIds.filter(id => !deleted.has(id)));
          result.deletedIds.forEach(id => {
            const profile = appRoot.model.customerProfileDtos.get(id);
            if (profile) appRoot.model.remove(profile);
          });
          appRoot.model.__runInStoreContext(() => appRoot.model.__queryCache.clear());
          return result;
        } finally {
          self.isDeleting = false;
          self.deletingCustomerIds.clear();
        }
      }),
      deleteCustomerProfile: flow(function* (customerId: string) {
        setIsLoading(true);
        try {
          const result: { deleteCustomerProfile: DeleteCustomerProfileResultDto } = yield getAppRoot(self).model
            .mutateDeleteCustomerProfile({ id: customerId })
            .currentPromise();

          if (result.deleteCustomerProfile?.success) {
            const index = self.queriedProfileIds.indexOf(customerId);
            if (index > -1) {
              self.queriedProfileIds.splice(index, 1);
            }

            const appRoot = getAppRoot(self);
            if (appRoot.ui.selectedCustomerProfile?.id === customerId) {
              appRoot.ui.selectCustomerProfile(null);
            }

            getAppEnv(self).notification(deleteCustomerProfileSuccessNotification());
            return result.deleteCustomerProfile;
          }
          return undefined;
        } catch (error) {
          getAppEnv(self).notification(deleteCustomerProfileErrorNotification());
          throw error;
        } finally {
          setIsLoading(false);
        }
      }),
    };
  })
  .views(self => ({
    get profiles(): GridValidRowModel[] {
      return self.queriedProfileIds
        .map(id => getAppRoot(self).model.customerProfileDtos?.get(id))
        .filter(row => row !== undefined && !row.deleted) as GridValidRowModel[];
    },
  }));

export default CustomerProfiles;
